import React, { useEffect, useMemo, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GetData from "../../../services/getData";
import logPatientChange from "../../../services/patientLogs";
import { buildPatientLogText } from "../../../services/patientLogMessages";
import BlockTitle from "../../../components/layout/blockTitle/BlockTitle";
import { Button } from "../../../components/ui";
import "../patients.css";
import "./PatientProfile.css";
import "./PatientSmartDiagnostics.css";

const TEMPLATE_ID = 22;

const FORM_DEFAULT = {
  study_type: "",
  device: "",
  bp: "",
  hr: "",
  spo2: "",
  auscultation: "",
  vascular_age: "",
  stiffness_index: "",
  reflection_index: "",
  other_indicators: "",
  height: "",
  weight: "",
  bmi: "",
  waist: "",
  hand_right: "",
  hand_left: "",
  urine_protein: "",
  urine_glucose: "",
  urine_ketones: "",
  urine_ph: "",
  urine_blood: "",
  urine_other: "",
  saliva_ph: "",
  metabolic_note: "",
  t_score: "",
  z_score: "",
  bone_conclusion: "",
  vaginal_ph: "",
  women_conclusion: "",
};

const getFormBlocks = (t) => [
  {
    title: null,
    items: [
      { name: "study_type", label: t("psd.fields.studyType") },
      { name: "device", label: t("psd.fields.device") },
    ],
  },
  {
    title: t("psd.blocks.heartVessels"),
    items: [
      { name: "bp", label: t("psd.fields.bp") },
      { name: "hr", label: t("psd.fields.hr") },
      { name: "spo2", label: t("psd.fields.spo2") },
      { name: "auscultation", label: t("psd.fields.auscultation") },
      { type: "subtitle", label: t("psd.blocks.angioscan") },
      { name: "vascular_age", label: t("psd.fields.vascularAge") },
      { name: "stiffness_index", label: t("psd.fields.stiffnessIndex") },
      { name: "reflection_index", label: t("psd.fields.reflectionIndex") },
      { name: "other_indicators", label: t("psd.fields.otherIndicators") },
    ],
  },
  {
    title: t("psd.blocks.bodyMuscle"),
    items: [
      { name: "height", label: t("psd.fields.height") },
      { name: "weight", label: t("psd.fields.weight") },
      { name: "bmi", label: t("psd.fields.bmi") },
      { name: "waist", label: t("psd.fields.waist") },
      { type: "subtitle", label: t("psd.blocks.dynamometry") },
      { name: "hand_right", label: t("psd.fields.handRight") },
      { name: "hand_left", label: t("psd.fields.handLeft") },
    ],
  },
  {
    title: t("psd.blocks.metabolic"),
    items: [
      { type: "subtitle", label: t("psd.blocks.urineExpress") },
      { name: "urine_protein", label: t("psd.fields.urineProtein") },
      { name: "urine_glucose", label: t("psd.fields.urineGlucose") },
      { name: "urine_ketones", label: t("psd.fields.urineKetones") },
      { name: "urine_ph", label: t("psd.fields.urinePh") },
      { name: "urine_blood", label: t("psd.fields.urineBlood") },
      { name: "urine_other", label: t("psd.fields.urineOther") },
      { name: "saliva_ph", label: t("psd.fields.salivaPh") },
      { name: "metabolic_note", label: t("psd.fields.metabolicNote") },
    ],
  },
  {
    title: t("psd.blocks.bone"),
    items: [
      { type: "subtitle", label: t("psd.blocks.densitometry") },
      { name: "t_score", label: t("psd.fields.tScore") },
      { name: "z_score", label: t("psd.fields.zScore") },
      { name: "bone_conclusion", label: t("psd.fields.boneConclusion") },
    ],
  },
  {
    title: t("psd.blocks.womenHealth"),
    items: [
      { name: "vaginal_ph", label: t("psd.fields.vaginalPh") },
      { name: "women_conclusion", label: t("psd.fields.womenConclusion") },
    ],
  },
];

const displayLinedValue = (value) => (!value && value !== 0 ? "—" : value);

const calcBmi = (weightKg, heightCm) => {
  const weight = Number.parseFloat(String(weightKg ?? "").replace(",", "."));
  const height = Number.parseFloat(String(heightCm ?? "").replace(",", "."));
  if (!Number.isFinite(weight) || !Number.isFinite(height) || weight <= 0 || height <= 0) {
    return "";
  }
  const bmi = weight / (height / 100) ** 2;
  return (Math.round(bmi * 10) / 10).toFixed(1);
};

const parseDocData = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return {};
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (_) {
      return {};
    }
  }
  return {};
};

const mapDocDataToFormData = (raw) => ({
  ...FORM_DEFAULT,
  ...parseDocData(raw),
});

const buildDocDataPayload = (form) => ({
  ...(form && typeof form === "object" ? form : {}),
});

function PatientSmartDiagnostics() {
  const { t } = useTranslation("patient");
  const formBlocks = useMemo(() => getFormBlocks(t), [t]);
  const navigate = useNavigate();
  const { patientId, docId } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ ...FORM_DEFAULT });
  const [savedFormData, setSavedFormData] = useState({ ...FORM_DEFAULT });
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const shouldAutoPrint = searchParams.get("print") === "1";

  useEffect(() => {
    loadDocument();
  }, [patientId, docId]);

  const showAlert = (message) => {
    setModalMessage(message);
    setShowModal(true);
  };

  const loadDocument = async () => {
    setLoading(true);
    setError(null);
    try {
      const docRes = await GetData({
        module: "patient",
        block: "patient_docs",
        action: "get",
        id_patient: patientId,
        id: docId,
      });

      if (docRes?.status === "fail") {
        throw new Error(docRes?.message || t("docs.errors.notFound"));
      }

      const doc = docRes?.result ?? null;
      if (!doc?.id) {
        throw new Error(t("docs.errors.notFound"));
      }
      if (Number(doc.template_id) !== TEMPLATE_ID) {
        throw new Error(t("psd.notSmartDiagnostics"));
      }

      const mapped = mapDocDataToFormData(doc.data);
      setFormData(mapped);
      setSavedFormData(mapped);
    } catch (err) {
      setError(err.message || t("docs.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "height" || name === "weight") {
        next.bmi = calcBmi(next.weight, next.height);
      }
      return next;
    });
  };

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    setFormData(savedFormData);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const payload = buildDocDataPayload(formData);
    try {
      const result = await GetData({
        module: "patient",
        block: "patient_docs",
        action: "update",
        id_patient: patientId,
        id: docId,
        data: payload,
      });

      if (result?.status === "fail") {
        throw new Error(result?.message || t("docs.errors.saveFailed"));
      }

      const mapped = mapDocDataToFormData(result?.result?.data ?? payload);
      setFormData(mapped);
      setSavedFormData(mapped);
      setIsEditing(false);
      showAlert(t("docs.errors.saved"));
    } catch (err) {
      console.error("Ошибка сохранения:", err);
      showAlert(t("docs.errors.saveFailedWith", { message: err.message }));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t("psd.confirmDelete"))) return;

    try {
      const result = await GetData({
        module: "patient",
        block: "patient_docs",
        action: "delete",
        id_patient: patientId,
        id: docId,
      });

      if (result?.status !== "success") {
        throw new Error(result?.message || t("docs.errors.deleteFailed"));
      }

      await logPatientChange({
        idPatient: patientId,
        idItem: docId,
        text: buildPatientLogText("patient_docs_deleted", {
          details: "Смарт Диагностика",
        }),
      });

      navigate(`/patient/${patientId}/profile`);
    } catch (err) {
      console.error("Ошибка удаления:", err);
      showAlert(t("docs.errors.deleteFailedWith", { message: err.message }));
    }
  };

  const handlePrint = () => {
    if (typeof window === "undefined") return;
    const body = document.body;
    if (!body) {
      window.print();
      return;
    }

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      body.classList.remove("print-only-profile", "print-profile");
      window.removeEventListener("afterprint", cleanup);
    };

    window.addEventListener("afterprint", cleanup);
    body.classList.add("print-only-profile", "print-profile");
    window.print();
  };

  useEffect(() => {
    if (!shouldAutoPrint || loading || error) return;
    const timer = window.setTimeout(() => {
      handlePrint();
      navigate(`/patient/${patientId}/profile/smart-diagnostics/${docId}`, {
        replace: true,
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [shouldAutoPrint, loading, error, patientId, docId, navigate]);

  if (loading) return <div className="loading">{t("state.loading", { ns: "common" })}</div>;
  if (error) return <div className="error">{t("error", { ns: "common", message: error })}</div>;

  return (
    <>
      <div className="patient-name-title-sticky">
        <BlockTitle
          title={t("psd.title")}
          buttons={
            isEditing
              ? [
                  {
                    label: t("docs.buttons.save"),
                    onClick: handleSave,
                    type: "normal",
                  },
                  {
                    label: t("docs.buttons.cancel"),
                    onClick: handleCancel,
                    type: "light",
                  },
                ]
              : [
                  {
                    label: t("docs.buttons.edit"),
                    onClick: handleEdit,
                    type: "light icon",
                  },
                  {
                    label: t("docs.buttons.delete"),
                    onClick: handleDelete,
                    type: "danger",
                  },
                  {
                    label: t("docs.buttons.backToList"),
                    onClick: () => navigate(`/patient/${patientId}/profile`),
                    type: "normal icon",
                  },
                ]
          }
        />
      </div>

      <Form className={`psd-form mt-4${isEditing ? " patient-profile--editing" : ""}`}>
        {formBlocks.map((block, blockIndex) => (
          <div
            className={`patient-life-anamnesis-block${block.title ? "" : " patient-life-anamnesis-block--headless"}`}
            key={block.title || `psd-block-${blockIndex}`}>
            {block.title ? (
              <Row className="mb-2">
                <Col md={12}>
                  <div className="patient-form-group">
                    <div className="patient-profile-group-title">{block.title}</div>
                  </div>
                </Col>
              </Row>
            ) : null}

            {block.items.map((item, itemIndex) =>
              item.type === "subtitle" ? (
                <Row className="mb-2" key={`${block.title || "header"}-sub-${itemIndex}`}>
                  <Col md={12}>
                    <div className="psd-subsection-title">{item.label}</div>
                  </Col>
                </Row>
              ) : (
                <Row className="mb-3" key={item.name}>
                  <Col md={12}>
                    <div className="patient-form-group">
                      <Form.Group controlId={`psd-${item.name}`}>
                        <div className="patient-inline-field">
                          <Form.Label className="patient-inline-label">
                            {item.label}:
                          </Form.Label>
                          {isEditing ? (
                            <Form.Control
                              className="patient-inline-value"
                              name={item.name}
                              value={formData[item.name] ?? ""}
                              onChange={handleChange}
                              placeholder=" "
                            />
                          ) : (
                            <div className="patient-inline-value">
                              {displayLinedValue(formData[item.name])}
                            </div>
                          )}
                        </div>
                      </Form.Group>
                    </div>
                  </Col>
                </Row>
              ),
            )}
          </div>
        ))}
      </Form>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("docs.errors.alertTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage}</Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowModal(false)} variant="primary">
            {t("actions.ok", { ns: "common" })}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default PatientSmartDiagnostics;
